/**
 * Feature flags.
 *
 * Modules that only some businesses need are switched on in Configuration
 * rather than shown to everyone. A single-entity proprietorship should never
 * see "Intercompany Elimination" in its navigation — an option that cannot
 * apply is noise at best and a support question at worst.
 *
 * Flags are stored on the entity's setup record, so they travel with the
 * entity and are auditable like any other configuration change.
 */

import type { ViewKey } from "./types";

export type FeatureKey =
  | "multiEntity"
  | "intercompany"
  | "msme"
  | "tds"
  | "tcs"
  | "gstReconciliation"
  | "inventory"
  | "foreignCurrency"
  | "payroll"
  | "fixedAssets"
  | "costCentres"
  | "budgets"
  | "eInvoice"
  | "eWayBill"
  | "creditNotes"
  | "recurringBilling";

export interface FeatureDefinition {
  key: FeatureKey;
  label: string;
  description: string;
  /** Configuration group this switch lives under. */
  group: string;
  /** Navigation entries hidden while the flag is off. */
  gatedViews?: ViewKey[];
  /** Flags that must be on for this one to be meaningful. */
  requires?: FeatureKey[];
  /** Sensible starting state for a new Indian entity. */
  defaultOn: boolean;
  /** Why turning this on matters — shown as helper text in Configuration. */
  guidance?: string;
}

export const FEATURES: FeatureDefinition[] = [
  {
    key: "multiEntity",
    label: "Multiple entities",
    description: "Maintain separate books for more than one legal entity in this workspace.",
    group: "Organisation",
    gatedViews: ["multiEntity"],
    defaultOn: false,
    guidance: "Turn on only when a second legal entity exists. Consolidation and eliminations depend on this."
  },
  {
    key: "intercompany",
    label: "Intercompany & eliminations",
    description: "Due-to/due-from tracking, counterparty confirmation and elimination journals.",
    group: "Organisation",
    requires: ["multiEntity"],
    defaultOn: false,
    guidance: "Requires multiple entities. Eliminations cannot be prepared for a single-entity workspace."
  },
  {
    key: "msme",
    label: "MSME tracking (Udyam / section 43B(h))",
    description: "Capture Udyam registration on vendors and track the 45-day payment limit.",
    group: "Statutory",
    defaultOn: true,
    guidance: "Payments to micro and small vendors beyond the agreed period are disallowed until paid under section 43B(h)."
  },
  {
    key: "tds",
    label: "TDS",
    description: "Section-wise deduction, challan tracking and Form 26Q/24Q preparation.",
    group: "Statutory",
    defaultOn: true,
    guidance: "Turn off only where the entity has no deduction obligation."
  },
  {
    key: "tcs",
    label: "TCS",
    description: "Collection at source under section 206C with Form 27EQ preparation.",
    group: "Statutory",
    defaultOn: false,
    guidance: "Applies mainly to sale of goods above threshold, scrap and specified goods."
  },
  {
    key: "gstReconciliation",
    label: "GST reconciliation",
    description: "GSTR-2B matching, ITC eligibility and return-versus-books comparison.",
    group: "Statutory",
    defaultOn: true
  },
  {
    key: "inventory",
    label: "Inventory",
    description: "Stock items, valuation and movement tracking.",
    group: "Operations",
    gatedViews: ["inventory"],
    defaultOn: false,
    guidance: "Service businesses should leave this off — it removes stock accounts from the chart."
  },
  {
    key: "foreignCurrency",
    label: "Foreign currency",
    description: "Transactions in currencies other than the base currency, with revaluation.",
    group: "Operations",
    defaultOn: false
  },
  { key: "payroll", label: "Payroll", description: "Salary processing with PF, ESI and professional tax.", group: "Operations", defaultOn: false },
  { key: "fixedAssets", label: "Fixed assets", description: "Asset register with Schedule II depreciation.", group: "Operations", defaultOn: true },
  { key: "costCentres", label: "Cost centres", description: "Tag postings to departments, projects or branches.", group: "Analytics", defaultOn: true },
  { key: "budgets", label: "Budgets", description: "Budget versus actual by account and cost centre.", group: "Analytics", defaultOn: false },
  {
    key: "eInvoice",
    label: "e-Invoicing (IRN)",
    description: "Generate IRN and signed QR through the Invoice Registration Portal.",
    group: "Statutory",
    defaultOn: false,
    guidance: "Mandatory above the notified aggregate turnover threshold."
  },
  { key: "eWayBill", label: "e-Way Bill", description: "Generate and track e-way bills for goods movement.", group: "Statutory", defaultOn: false },
  { key: "creditNotes", label: "Credit & debit notes", description: "Raise credit notes against invoices with GST adjustment.", group: "Invoicing", defaultOn: true },
  { key: "recurringBilling", label: "Recurring billing", description: "Scheduled invoices for retainers and subscriptions.", group: "Invoicing", defaultOn: false }
];

export const FEATURE_INDEX = new Map(FEATURES.map((feature) => [feature.key, feature]));

export type FeatureFlags = Partial<Record<FeatureKey, boolean>>;

export const defaultFeatureFlags = (): FeatureFlags =>
  Object.fromEntries(FEATURES.map((feature) => [feature.key, feature.defaultOn])) as FeatureFlags;

/**
 * Resolves a flag, honouring dependencies. A feature whose prerequisite is off
 * is itself off regardless of its own stored value — otherwise turning off
 * "Multiple entities" would leave "Intercompany" stranded and visible.
 */
export const isFeatureOn = (flags: FeatureFlags | undefined, key: FeatureKey): boolean => {
  const definition = FEATURE_INDEX.get(key);
  if (!definition) return false;
  const own = flags?.[key] ?? definition.defaultOn;
  if (!own) return false;
  return (definition.requires ?? []).every((requirement) => isFeatureOn(flags, requirement));
};

/** Views hidden because the feature that owns them is off. */
export const hiddenViews = (flags: FeatureFlags | undefined): Set<ViewKey> => {
  const hidden = new Set<ViewKey>();
  for (const feature of FEATURES) {
    if (!feature.gatedViews?.length) continue;
    if (!isFeatureOn(flags, feature.key)) feature.gatedViews.forEach((view) => hidden.add(view));
  }
  return hidden;
};

export const isViewVisible = (flags: FeatureFlags | undefined, view: ViewKey): boolean =>
  !hiddenViews(flags).has(view);

/**
 * Turning a feature off also turns off everything that depends on it, so the
 * stored state can never describe an impossible configuration.
 */
export const setFeature = (flags: FeatureFlags, key: FeatureKey, on: boolean): FeatureFlags => {
  const next: FeatureFlags = { ...flags, [key]: on };
  if (!on) {
    for (const feature of FEATURES) {
      if (feature.requires?.includes(key)) next[feature.key] = false;
    }
  }
  return next;
};

/** Grouped for rendering in the Configuration screen. */
export const featureGroups = (): Array<{ group: string; features: FeatureDefinition[] }> => {
  const groups = new Map<string, FeatureDefinition[]>();
  for (const feature of FEATURES) {
    const bucket = groups.get(feature.group) ?? [];
    bucket.push(feature);
    groups.set(feature.group, bucket);
  }
  return [...groups.entries()].map(([group, features]) => ({ group, features }));
};
